import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie, ChevronDown } from 'lucide-react';
import { hasDecided, setConsent, rehydrateGtagConsent } from '@/lib/cookieConsent';

// Redesigned per design.md. All consent logic (setConsent/hasDecided/
// rehydrateGtagConsent calls) untouched - only JSX/className changed.
// Removed the custom box-shadow (spec has zero tolerance for shadows -
// "Any drop shadow would immediately break the editorial gallery feel"),
// the rounded-2xl card, and the rounded-full icon circle. Both action
// buttons converted to text-only links rather than repeating the one
// filled-button exception made for the Contact form's OTP flow - this is
// a simple binary choice, not a validation-gated transactional action, so
// there's a weaker case for a filled control here than there was there.

export const CookieConsent = () => {
  const [visible, setVisible] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);

  useEffect(() => {
    rehydrateGtagConsent();

    if (!hasDecided()) {
      const t = setTimeout(() => setVisible(true), 1200);
      return () => clearTimeout(t);
    }
  }, []);

  const accept = () => {
    setConsent({ calendly: true, analytics: true });
    setVisible(false);
  };

  const necessaryOnly = () => {
    setConsent({ calendly: false, analytics: false });
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          className="fixed bottom-4 left-4 right-4 md:left-6 md:right-auto md:max-w-md z-[60]"
          data-testid="cookie-consent-banner"
          role="dialog"
          aria-live="polite"
          aria-label="Cookie consent"
        >
          <div className="bg-paper border border-fog rounded-editorial p-6">
            <div className="flex items-start gap-3 mb-3">
              <Cookie className="w-5 h-5 text-stone shrink-0 mt-0.5" strokeWidth={1.5} />
              <div className="font-bentonsans">
                <p className="text-body text-ink leading-snug">
                  We use a couple of cookies
                </p>
                <p className="text-caption text-graphite leading-[1.4] mt-1">
                  Essential ones keep the site running. With your consent we
                  also use Google Analytics and load our booking widget
                  (Calendly), both of which set their own cookies.
                </p>
              </div>
            </div>

            <AnimatePresence>
              {detailsOpen && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="overflow-hidden"
                >
                  <div className="font-bentonsans text-caption text-graphite leading-[1.4] space-y-2 pb-3 pl-8">
                    <p>
                      <span className="text-ink">Essential</span> — required for the site to function (e.g. remembering your live chat conversation). Always on.
                    </p>
                    <p>
                      <span className="text-ink">Analytics (Google Analytics)</span> — helps us see which pages are useful and fix ones that aren't. No storage happens until you accept.
                    </p>
                    <p>
                      <span className="text-ink">Booking widget (Calendly)</span> — only loads on the consultation page, and only if you accept. Lets you book a call without leaving the site.
                    </p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <button
              onClick={() => setDetailsOpen((v) => !v)}
              className="font-bentonsans text-caption text-stone hover:text-ink transition-colors inline-flex items-center gap-1 mb-4 pl-8"
              data-testid="cookie-consent-details-toggle"
            >
              {detailsOpen ? 'Hide details' : 'What does this mean?'}
              <ChevronDown
                className={`w-3 h-3 transition-transform ${detailsOpen ? 'rotate-180' : ''}`}
              />
            </button>

            <div className="flex gap-6 pl-8 font-bentonsans text-body">
              <button
                onClick={accept}
                className="text-ink hover:text-stone transition-colors"
                data-testid="cookie-consent-accept"
              >
                Accept all
              </button>
              <button
                onClick={necessaryOnly}
                className="text-graphite hover:text-ink transition-colors"
                data-testid="cookie-consent-necessary"
              >
                Necessary only
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
